import { useEffect, useState } from "react";
import { Navigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { DayHours, StoreHoursPublic } from "@passa-a-pagina/shared";
import { api, ApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";

// Monday-first i18n keys (index 0 = Monday … 6 = Sunday), matching the
// server's day ordering.
const DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

const DEFAULT_DAY: NonNullable<DayHours> = { open: "09:00", close: "17:00" };

export function AdminHours() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const [days, setDays] = useState<DayHours[] | null>(null);
  const [flash, setFlash] = useState<{ type: "ok" | "err"; msg: string } | null>(null);

  const hours = useQuery<StoreHoursPublic, ApiError>({
    queryKey: ["store-hours"],
    queryFn: () => api<StoreHoursPublic>("/api/store-hours"),
    retry: false,
  });

  // Seed the editable form once the saved hours arrive.
  useEffect(() => {
    if (hours.data && days === null) setDays(hours.data.days);
  }, [hours.data, days]);

  const save = useMutation({
    mutationFn: (payload: DayHours[]) =>
      api<{ ok: true; display: string }>("/api/admin/store-hours", {
        method: "PUT",
        body: { days: payload },
      }),
    onSuccess: (res) => {
      setFlash({ type: "ok", msg: t("admin.hours.saved", { display: res.display }) });
      // Refresh anywhere the hours are shown (footer, appointments, this page).
      qc.invalidateQueries({ queryKey: ["site-info"] });
      qc.invalidateQueries({ queryKey: ["store-hours"] });
    },
    onError: (err: ApiError) => setFlash({ type: "err", msg: err.message }),
  });

  if (loading) return <div className="container"><p>{t("common.loading")}</p></div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  function setDay(index: number, value: DayHours) {
    setDays((prev) => {
      if (!prev) return prev;
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  function toggleClosed(index: number, closed: boolean) {
    setDay(index, closed ? null : { ...DEFAULT_DAY });
  }

  function onSave() {
    if (!days) return;
    setFlash(null);
    // Client-side mirror of the server rule for instant feedback.
    for (let i = 0; i < days.length; i++) {
      const d = days[i];
      if (d && d.open >= d.close) {
        setFlash({ type: "err", msg: t("admin.hours.errOrder", { day: t(`admin.hours.days.${DAY_KEYS[i]}`) }) });
        return;
      }
    }
    save.mutate(days);
  }

  return (
    <section aria-labelledby="hours-title" className="admin__panel">
      <header className="inventory__head">
        <div>
          <h2 id="hours-title">{t("admin.hours.title")}</h2>
          <p>{t("admin.hours.intro")}</p>
        </div>
      </header>

      {flash && (
        <div className={`inventory__flash inventory__flash--${flash.type}`} role="alert" aria-live="polite">
          {flash.msg}
        </div>
      )}

      {hours.isLoading || days === null ? (
        <p>{t("common.loading")}</p>
      ) : hours.error ? (
        <p className="field__error" role="alert">{hours.error.message}</p>
      ) : (
        <div className="surface hours-editor">
          <ul className="hours-editor__list">
            {days.map((d, i) => {
              const closed = d === null;
              const dayLabel = t(`admin.hours.days.${DAY_KEYS[i]}`);
              return (
                <li key={DAY_KEYS[i]} className="hours-editor__row">
                  <span className="hours-editor__day">{dayLabel}</span>
                  <label className="hours-editor__closed">
                    <input
                      type="checkbox"
                      checked={closed}
                      onChange={(e) => toggleClosed(i, e.target.checked)}
                      disabled={save.isPending}
                    />
                    <span>{t("admin.hours.closed")}</span>
                  </label>
                  <div className="hours-editor__times" aria-hidden={closed}>
                    <label>
                      <span className="hours-editor__times-label">{t("admin.hours.open")}</span>
                      <input
                        type="time"
                        value={d?.open ?? DEFAULT_DAY.open}
                        disabled={closed || save.isPending}
                        onChange={(e) => setDay(i, { open: e.target.value, close: d?.close ?? DEFAULT_DAY.close })}
                        aria-label={t("admin.hours.openAria", { day: dayLabel })}
                      />
                    </label>
                    <span className="hours-editor__dash" aria-hidden="true">–</span>
                    <label>
                      <span className="hours-editor__times-label">{t("admin.hours.close")}</span>
                      <input
                        type="time"
                        value={d?.close ?? DEFAULT_DAY.close}
                        disabled={closed || save.isPending}
                        onChange={(e) => setDay(i, { open: d?.open ?? DEFAULT_DAY.open, close: e.target.value })}
                        aria-label={t("admin.hours.closeAria", { day: dayLabel })}
                      />
                    </label>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="inventory__actions">
            <button type="button" className="btn btn-primary" onClick={onSave} disabled={save.isPending}>
              {save.isPending ? t("admin.hours.saving") : t("admin.hours.save")}
            </button>
          </div>
          <p className="hours-editor__hint">{t("admin.hours.previewHint", { display: hours.data?.display ?? "" })}</p>
        </div>
      )}
    </section>
  );
}
